<script setup lang="ts">
import { watch, nextTick, ref } from 'vue'
import { AlertTriangle, Info, AlertCircle } from 'lucide-vue-next'
import { useConfirmDialog } from '@/core/composables/useConfirmDialog'

const { state, handleConfirm, handleCancel } = useConfirmDialog()

const confirmBtn = ref<HTMLButtonElement | null>(null)

// Focus confirm button when dialog opens
watch(() => state.value.visible, async (visible) => {
  if (visible) {
    await nextTick()
    confirmBtn.value?.focus()
  }
})

// Handle Escape key
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    handleCancel()
  }
}

const variantConfig = {
  danger: {
    icon: AlertTriangle,
    iconClass: 'text-red-500',
    bgClass: 'bg-red-50 dark:bg-red-900/20',
    btnClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
  },
  warning: {
    icon: AlertCircle,
    iconClass: 'text-amber-500',
    bgClass: 'bg-amber-50 dark:bg-amber-900/20',
    btnClass: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
  },
  info: {
    icon: Info,
    iconClass: 'text-blue-500',
    bgClass: 'bg-blue-50 dark:bg-blue-900/20',
    btnClass: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
  },
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="state.visible"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4"
        @keydown="onKeydown"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/50 backdrop-blur-sm"
          @click="handleCancel"
        />

        <!-- Dialog -->
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="state.visible"
            class="relative w-full max-w-md rounded-xl bg-white shadow-2xl dark:bg-slate-800"
            role="alertdialog"
            aria-modal="true"
            :aria-labelledby="'confirm-title'"
            :aria-describedby="'confirm-message'"
          >
            <div class="p-6">
              <!-- Icon + Title -->
              <div class="flex items-start gap-4">
                <div
                  class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                  :class="variantConfig[state.variant].bgClass"
                >
                  <component
                    :is="variantConfig[state.variant].icon"
                    class="h-5 w-5"
                    :class="variantConfig[state.variant].iconClass"
                  />
                </div>
                <div class="min-w-0">
                  <h3
                    id="confirm-title"
                    class="text-lg font-semibold text-slate-900 dark:text-white"
                  >
                    {{ state.title }}
                  </h3>
                  <p
                    id="confirm-message"
                    class="mt-2 text-sm text-slate-600 dark:text-slate-300 whitespace-pre-line"
                  >
                    {{ state.message }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex justify-end gap-3 border-t border-slate-200 px-6 py-4 dark:border-slate-700">
              <button
                type="button"
                class="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:text-slate-300 dark:hover:bg-slate-700"
                @click="handleCancel"
              >
                {{ state.cancelText }}
              </button>
              <button
                ref="confirmBtn"
                type="button"
                class="rounded-lg px-4 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                :class="variantConfig[state.variant].btnClass"
                @click="handleConfirm"
              >
                {{ state.confirmText }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
